import React from "react"
import { useTranslation } from "react-i18next"

import { useAuth } from "hooks/useAuth"
import FormElement from "components/Form/FormElement"
import { genderMFCList } from "utils/constants"
import { LocalEntity } from "models"
import { FormError } from "models/form"

type ServicesFormProps = {
	partner: LocalEntity
	setUpdatedPartner: React.Dispatch<React.SetStateAction<LocalEntity>>
	isMFCSelected: boolean
	setErrors: React.Dispatch<React.SetStateAction<Array<FormError>>>
	isEdit: boolean
	showLocalErrors: boolean
}

const defaultFormFields = {
	mfc_client_number: "",
	gender_mfc: null,
}

export const ServicesForm = ({
	partner,
	setUpdatedPartner,
	isMFCSelected,
	setErrors,
	isEdit,
	showLocalErrors,
}: ServicesFormProps) => {
	const { t } = useTranslation()
	const { isAdminOCode, isAdminMFC } = useAuth()
	const [formFields, setFormFields] = React.useState<Partial<LocalEntity>>(defaultFormFields)

	React.useEffect(
		() => {
			// When partner reloaded (after await fetchData()), set data in formFields
			if (partner || !isEdit) setFormFields(partner)
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[partner, isEdit]
	)

	React.useEffect(
		() => {
			// When we create a new partner, we have to pass data to parent directly.
			setUpdatedPartner((prevFormFields) => ({
				...prevFormFields,
				mfc_client_number: formFields.mfc_client_number,
				gender_mfc: formFields.gender_mfc,
			}))
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[formFields]
	)

	const handleInputChange = React.useCallback((event) => {
		const target = event.target
		const value = target.type === "checkbox" ? target.checked : target.value
		const name = target.name
		setFormFields((s) => ({
			...s,
			[name]: value,
		}))
	}, [])

	const handleValidation = React.useCallback((name, errs) => {
		setErrors((s) => {
			const cleanErrors = [...s.filter((e) => e.origin !== name)]
			return [...cleanErrors, ...errs]
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	React.useEffect(
		() => {
			setUpdatedPartner((prevFormFields) => ({
				...prevFormFields,
				mfc_client_number: formFields.mfc_client_number,
				gender_mfc: Number(formFields.gender_mfc),
			}))
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[formFields]
	)

	const formElementCommonProps = {
		showErrors: showLocalErrors,
		onValidate: handleValidation,
		onChange: handleInputChange,
		readonly: !isEdit,
		required: false,
	}

	return (
		<section id="services" className="py-16 px-4">
			{(isAdminMFC || (isAdminOCode && formFields.isMFC) || isMFCSelected) && (
				<section>
					<h2 className="c-h2 font-semibold mb-7 uppercase">
						{t("edit.provide_services", { ns: "partner" })}{" "}
						{isAdminOCode && " " + t("operators.mfc")}
					</h2>
					<div className="l-grid u-mg-negative-hz-m">
						<div className="lg:w-1/2 w-full px-5">
							{/* numéro série */}
							<FormElement
								{...formElementCommonProps}
								value={formFields.mfc_client_number}
								name="mfc_client_number"
								type="text"
								label={t("edit.client_number", { ns: "partner" })}
								className="mb-5"
							/>
						</div>
						<div className="lg:w-1/2 w-full lg:pl-24 px-5">
							{/* Type de partenaire */}
							<FormElement
								{...formElementCommonProps}
								value={formFields.gender_mfc}
								name="gender_mfc"
								type="radio"
								options={genderMFCList}
								classNameOptionsContainer="flex-col w-full"
								classNameLabel="mb-5"
								classNameOption="flex-1 items-center"
								label={t("specificity", { ns: "partner" })}
							/>
						</div>
					</div>
				</section>
			)}
		</section>
	)
}
